import { createSelector } from '@reduxjs/toolkit';
import { shallowEqual } from 'react-redux';

import sameArrayResult from 'editor/src/store/design/util/sameArrayResult';
import { getSharedTextFieldGroups, receivesSharedValue } from 'editor/src/store/design/util/sharedTextFields';
import { RootState } from 'editor/src/store/index';

export interface SharedValueMember {
  uuid: number;
  name: string;
  spreadName: string;
  spreadIndex: number;
  /** Prints, but never takes the group's value — locked, or keeping a string
   *  per picklist option. Carried so a summary can name the layer without
   *  claiming the buyer's typing reaches it. */
  receivesValue: boolean;
}

export interface SharedValueGroup {
  sharedFieldId: string;
  /** The one value the group holds. Read off a member that receives it: a
   *  locked member can be sitting on the string it had before it was locked,
   *  and showing that as the group's value would name a value nothing else
   *  holds. */
  value: string;
  members: SharedValueMember[];
}

const NONE: SharedValueGroup[] = [];

const selectDesignData = (state: RootState) => state.design.designData;

// See sameArrayResult. Unlike getSharedTextFieldTargets, `value` is part of
// what is compared here, so a keystroke in a shared field DOES rebuild — which
// is right, the summary shows the value.
const sameGroups = sameArrayResult<SharedValueGroup>(
  (group, other) =>
    group.sharedFieldId === other.sharedFieldId &&
    group.value === other.value &&
    group.members.length === other.members.length &&
    group.members.every((member, memberIndex) => shallowEqual(member, other.members[memberIndex])),
);

/**
 * Every group of layers holding one value, for the author's summary.
 *
 * The per-layer panel answers "what is THIS tied to", one layer at a time and
 * only while that layer is selected. This answers the question the author
 * actually asks of a finished design — "what is tied to what" — in one place,
 * and gives every tie somewhere to be undone from.
 */
const getSharedTextFieldSummary = createSelector(
  [selectDesignData],
  (designData): SharedValueGroup[] => {
    const groups = getSharedTextFieldGroups(designData).map(({ sharedFieldId, members }) => ({
      sharedFieldId,
      value: (members.find(({ element }) => receivesSharedValue(element)) ?? members[0]).element.extra.text,
      members: members.map(({ element, address }) => ({
        uuid: element.uuid,
        name: element.name,
        spreadName: designData?.spreads[address.spreadIndex]?.name ?? '',
        spreadIndex: address.spreadIndex,
        receivesValue: receivesSharedValue(element),
      })),
    }));

    return groups.length ? groups : NONE;
  },
  { memoizeOptions: { resultEqualityCheck: sameGroups } },
);

export default getSharedTextFieldSummary;
